import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Clock, ImagePlus, Search, Sparkles, X } from "lucide-react";
import { api, discountOf, fileUrl, fmt } from "@/lib/api";
import { useShop } from "@/context/ShopContext";

const RECENT_KEY = "kb_recent_searches";
const PRICE_FILTERS = [
  { v: "all", label: "Tous les prix", test: () => true },
  { v: "lt15", label: "Moins de 15 €", test: (p) => (p.price ?? 0) < 15 },
  { v: "15-30", label: "15 – 30 €", test: (p) => (p.price ?? 0) >= 15 && (p.price ?? 0) <= 30 },
  { v: "gt30", label: "Plus de 30 €", test: (p) => (p.price ?? 0) > 30 },
];

const loadRecent = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
  } catch {
    return [];
  }
};

function ResultCard({ p, index, onPick }) {
  const d = discountOf(p);
  return (
    <motion.button
      type="button"
      data-testid={`search-result-${p.id}`}
      onClick={() => onPick(p)}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index, 8) * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="group text-left rounded-2xl border border-kay-line bg-white overflow-hidden shadow-[0_10px_30px_rgba(232,160,191,0.10)] hover:shadow-[0_18px_40px_rgba(224,82,151,0.22)] hover:-translate-y-1 transition-all duration-300"
    >
      <div className="relative aspect-square bg-kay-rose/30 overflow-hidden">
        {p.images?.[0] ? (
          <img src={fileUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="absolute inset-2.5 border border-dashed border-kay-rosedeep/50 rounded-xl flex items-center justify-center">
            <ImagePlus className="text-kay-pink/60" size={22} strokeWidth={1.5} />
          </div>
        )}
        {d > 0 && (
          <span className="absolute top-2.5 left-2.5 bg-kay-pink text-white font-mono text-[10px] font-bold tracking-widest px-2 py-0.5 rounded-full" data-testid={`search-discount-${p.id}`}>
            -{d} %
          </span>
        )}
      </div>
      <div className="p-3.5">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kay-pink truncate">{p.brand}</p>
        <p className="font-display text-base leading-tight mt-0.5 line-clamp-1">{p.name}</p>
        <div className="mt-1.5 flex items-baseline gap-2">
          <span className="font-display text-lg font-semibold">{fmt(p.price)}</span>
          {p.reference_price > p.price && <span className="font-mono text-[11px] text-kay-ink/40 line-through">{fmt(p.reference_price)}</span>}
        </div>
      </div>
    </motion.button>
  );
}

export default function SearchOverlay({ open, onClose }) {
  const navigate = useNavigate();
  const { settings } = useShop() || {};
  const [products, setProducts] = useState(null);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  const [brand, setBrand] = useState("all");
  const [price, setPrice] = useState("all");
  const [promoOnly, setPromoOnly] = useState(false);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRecent(loadRecent());
    setQuery("");
    document.body.style.overflow = "hidden";
    if (!products) api.get("/products").then((r) => setProducts(r.data.filter((p) => !p.coming_soon))).catch(() => setProducts([]));
    const t = setTimeout(() => inputRef.current?.focus(), 250);
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const brands = useMemo(() => [...new Set((products || []).map((p) => p.brand).filter(Boolean))].sort(), [products]);
  const popular = useMemo(
    () => [...(products || [])].sort((a, b) => discountOf(b) - discountOf(a) || Number(b.is_new) - Number(a.is_new)).slice(0, 4),
    [products]
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const priceTest = PRICE_FILTERS.find((f) => f.v === price)?.test || (() => true);
    return (products || []).filter((p) => {
      if (cat !== "all" && p.category !== cat && p.subcategory !== cat) return false;
      if (brand !== "all" && p.brand !== brand) return false;
      if (promoOnly && discountOf(p) <= 0) return false;
      if (!priceTest(p)) return false;
      if (!q) return true;
      const hay = `${p.name} ${p.brand} ${p.description || ""} ${(p.variants || []).map((v) => v.name).join(" ")}`.toLowerCase();
      return q.split(/\s+/).every((w) => hay.includes(w));
    });
  }, [products, query, cat, brand, price, promoOnly]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const names = (products || []).filter((p) => p.name.toLowerCase().includes(q)).map((p) => p.name);
    const bs = brands.filter((b) => b.toLowerCase().includes(q));
    return [...new Set([...bs, ...names])].slice(0, 5);
  }, [products, brands, query]);

  const saveRecent = (term) => {
    const t = term.trim();
    if (!t) return;
    const next = [t, ...loadRecent().filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, 6);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    setRecent(next);
  };
  const removeRecent = (term) => {
    const next = loadRecent().filter((x) => x !== term);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    setRecent(next);
  };

  const pick = (p) => {
    if (query.trim()) saveRecent(query);
    onClose();
    navigate(`/produit/${p.id}`);
  };

  const chip = (active) =>
    `rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${
      active ? "bg-kay-ink text-white border border-kay-ink" : "bg-white/80 border border-kay-line hover:border-kay-ink"
    }`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[70] bg-kay-cream/95 backdrop-blur-xl overflow-y-auto grain"
        >
          <div className="max-w-4xl mx-auto px-5 pt-6 pb-16">
            <div className="flex items-center justify-between">
              <p className="font-display text-2xl font-semibold tracking-tight">
                KAY <span className="italic text-kay-pink">BEAUTY</span>
              </p>
              <button data-testid="search-close" onClick={onClose} aria-label="Fermer la recherche" className="w-11 h-11 rounded-full border border-kay-line bg-white flex items-center justify-center hover:bg-kay-ink hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.08, ease: [0.22, 1, 0.36, 1] }} className="mt-10">
              <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-4 text-center">Que cherches-tu ?</p>
              <div className="relative">
                <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-kay-pink" size={22} />
                <input
                  ref={inputRef}
                  data-testid="search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveRecent(query)}
                  placeholder="Un blush, une marque, une teinte…"
                  className="w-full rounded-full border-2 border-kay-line bg-white px-14 py-5 font-display text-xl sm:text-2xl outline-none focus:border-kay-pink transition-colors shadow-[0_20px_60px_rgba(232,160,191,0.25)]"
                />
                {query && (
                  <button data-testid="search-clear" onClick={() => setQuery("")} aria-label="Effacer" className="absolute right-5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-kay-rose/60 flex items-center justify-center hover:bg-kay-pink hover:text-white transition-colors">
                    <X size={14} />
                  </button>
                )}
              </div>

              {suggestions.length > 0 && (
                <div className="mt-3 flex flex-wrap justify-center gap-2" data-testid="search-suggestions">
                  {suggestions.map((s) => (
                    <button key={s} data-testid={`search-suggestion-${s.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setQuery(s)} className="rounded-full bg-white border border-kay-line px-4 py-1.5 text-xs font-semibold text-kay-ink/70 hover:border-kay-pink hover:text-kay-pink transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {query.trim() && (
              <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="mt-8 grid gap-2" data-testid="search-filters">
                <div className="flex flex-wrap gap-2 justify-center">
                  <button data-testid="search-cat-all" onClick={() => setCat("all")} className={chip(cat === "all")}>Tout</button>
                  {(settings?.categories || []).map((c) => (
                    <button key={c.slug} data-testid={`search-cat-${c.slug}`} onClick={() => setCat(c.slug)} className={chip(cat === c.slug)}>{c.label}</button>
                  ))}
                  {(settings?.subcategories || []).map((s) => (
                    <button key={s.slug} data-testid={`search-cat-${s.slug}`} onClick={() => setCat(s.slug)} className={chip(cat === s.slug)}>{s.label}</button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 justify-center">
                  <button data-testid="search-brand-all" onClick={() => setBrand("all")} className={chip(brand === "all")}>Toutes marques</button>
                  {brands.map((b) => (
                    <button key={b} data-testid={`search-brand-${b.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setBrand(b)} className={chip(brand === b)}>{b}</button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 justify-center">
                  {PRICE_FILTERS.map((f) => (
                    <button key={f.v} data-testid={`search-price-${f.v}`} onClick={() => setPrice(f.v)} className={chip(price === f.v)}>{f.label}</button>
                  ))}
                  <button data-testid="search-promo-toggle" onClick={() => setPromoOnly(!promoOnly)} className={`${chip(promoOnly)} ${promoOnly ? "" : "text-kay-pink border-dashed border-kay-rosedeep"}`}>
                    En promo uniquement
                  </button>
                </div>
              </motion.div>
            )}

            {!query.trim() && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mt-12 grid gap-10">
                {recent.length > 0 && (
                  <div data-testid="search-recent">
                    <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-kay-ink/50 mb-4 flex items-center gap-2">
                      <Clock size={13} className="text-kay-pink" /> Recherches récentes
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {recent.map((r) => (
                        <span key={r} className="group inline-flex items-center gap-1 rounded-full bg-white border border-kay-line pl-4 pr-1.5 py-1.5 text-xs font-semibold">
                          <button data-testid={`search-recent-${r.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setQuery(r)} className="hover:text-kay-pink transition-colors">{r}</button>
                          <button data-testid={`search-recent-remove-${r.toLowerCase().replace(/\s/g, "-")}`} onClick={() => removeRecent(r)} aria-label={`Retirer ${r}`} className="w-5 h-5 rounded-full flex items-center justify-center text-kay-ink/30 hover:bg-kay-rose hover:text-kay-pink transition-colors">
                            <X size={11} />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-kay-ink/50 mb-4 flex items-center gap-2">
                    <Sparkles size={13} className="text-kay-pink" /> Les pépites populaires
                  </p>
                  {products === null ? (
                    <p className="font-display italic text-xl text-kay-ink/40" data-testid="search-loading">Chargement…</p>
                  ) : (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="search-popular">
                      {popular.map((p, i) => (
                        <ResultCard key={p.id} p={p} index={i} onPick={pick} />
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {query.trim() && (
              <div className="mt-10" data-testid="search-results">
                <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-kay-ink/50 mb-5" data-testid="search-count">
                  {results.length} résultat{results.length > 1 ? "s" : ""} pour « {query.trim()} »
                </p>
                {results.length > 0 ? (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {results.map((p, i) => (
                      <ResultCard key={p.id} p={p} index={i} onPick={pick} />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-3xl border border-dashed border-kay-rosedeep/60 bg-white/70 py-16 px-8 text-center" data-testid="search-empty">
                    <p className="font-display italic text-3xl text-kay-ink/60">Aucune pépite ne correspond…</p>
                    <p className="mt-3 text-sm text-kay-ink/50 max-w-sm mx-auto">
                      Essaie un autre mot, une marque, ou propose-nous le produit de tes rêves : on le dénichera pour toi.
                    </p>
                    <button
                      data-testid="search-empty-ideas"
                      onClick={() => { saveRecent(query); onClose(); navigate("/idees"); }}
                      className="mt-7 inline-flex items-center gap-2 rounded-full bg-kay-ink text-white px-7 py-3 text-sm font-semibold hover:bg-kay-pink transition-colors"
                    >
                      Proposer ce produit <ArrowRight size={14} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
